// @flow
import InlineEdit from '@atlaskit/inline-edit';
import Spinner from '@atlaskit/spinner';
import { SimpleTag } from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import { isEqual, throttle } from 'lodash';
import React, { useEffect, useState, useCallback } from 'react';
import { compose, graphql, Query, WithApolloClient } from 'react-apollo';
import styled from 'styled-components';
import { OptionsType } from '@atlaskit/select';
import { UPDATE_JIRA_ISSUE } from '../../graphql/mutations';
import { JIRA_ISSUE } from '../../graphql/queries';
import { analytics } from '../../services/analytics';
import Frame from '../../services/iframe';
import VirtualComponentSelect, { parseVirtualComponentToOption } from '../select/VirtualComponentSelect';
import useJiraContext from '../../hooks/useJiraContext';

const throttledFrameAdjuster = throttle(Frame.adjustFrameHeight, 100, { leading: true });

const Wrapper = styled.div`
    margin: 0px 1px;
    display: flex;
    form {
        margin-top: -7px !important;
        width: 100%;
    }
    .sc-esjQYD {
        width: 100%;
    }
    /* InlineEdit action buttons are position:absolute by default; body overflow-y:hidden clips them in iframe. Put them in flow. */
    form > div > div > div:last-child {
        position: relative !important;
        justify-content: flex-end;
    }
`;

const DefaultView = styled.div`
    word-break: break-word;
`;

const DefaultNoneView = styled.div`
    word-break: break-word;
    padding: 8px 0 8px 0;
`;

const NoneView = styled.span`
    color: #5e6c84;
`;

const Loading = () => {
    return (
        <div style={{ padding: '7px' }}>
            <Spinner size="small" />
        </div>
    );
};

type Props = {
    componentParentsDefaultOptions?: OptionsType[],
};

const LinkComponents = ({ updateJiraIssue, componentParentsDefaultOptions }: WithApolloClient<Props>) => {
    const { issueId, projectKey } = useJiraContext();

    const [issue, setIssue] = useState(null);
    const [linkedComponentsOnEdit, setLinkedComponentsOnEdit] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const observer = new MutationObserver(throttledFrameAdjuster);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (issue) {
            setLinkedComponentsOnEdit(
                (issue.fields.components ?? []).map((comp) => ({
                    value: comp.virtualComponentId,
                    label: comp.name,
                    projects: [{ jiraComponentId: comp.id, jiraProjectKey: projectKey }],
                })),
            );
        }
    }, [issue, projectKey]);

    const handleConfirm = useCallback(() => {
        setLoading(true);

        const { components, componentsWithExtras, componentsIds } = linkedComponentsOnEdit.reduce(
            (acc, curr) => {
                const compProj = curr.projects.find((p) => p.jiraProjectKey === projectKey);
                const component = { id: compProj.jiraComponentId, name: curr.label };
                acc.components.push(component);
                acc.componentsWithExtras.push({
                    ...component,
                    virtualComponentId: compProj.jiraComponentId,
                });
                acc.componentsIds.push(component.id);

                return acc;
            },
            { components: [], componentsWithExtras: [], componentsIds: [] },
        );
        const issueComponentsIds = (issue.fields.components ?? []).map(({ id }) => id);

        if (isEqual(componentsIds, issueComponentsIds)) {
            setLoading(false);
            return;
        }

        updateJiraIssue({
            variables: {
                id: issueId,
                body: {
                    update: {
                        components: [{ set: components }],
                    },
                },
            },
        })
            .then(() => {
                const newIssue = { ...issue };
                newIssue.fields.components = componentsWithExtras;
                setIssue(newIssue);
                analytics.track({
                    userId: window.license,
                    event: 'updated_nextgen_field',
                    timestamp: new Date(),
                    properties: {
                        addon_key: process.env.REACT_APP_KEY,
                    },
                });
                // reload jira page
                window.PlatformAdapter.refreshJiraIssuePage();
            })
            .finally(() => setLoading(false));
    }, [issue, issueId, linkedComponentsOnEdit, projectKey, updateJiraIssue]);

    const handleCancel = useCallback(() => {
        // this forces the reload to previous values
        setIssue((prev) => ({ ...prev }));
    }, []);

    const onLoadJiraIssue = useCallback((data) => {
        setIssue(data.jiraIssue);
    }, []);

    if (!issueId || loading) {
        return <Loading />;
    }

    return (
        <Wrapper id="link-component-wrapper">
            <Query
                displayName="JiraIssue"
                skip={!issueId}
                query={JIRA_ISSUE}
                variables={{ id: issueId, includeVirtualComponentIds: true }}
                onCompleted={onLoadJiraIssue}
                notifyOnNetworkStatusChange={true}
            >
                {({ loading: jiraIssueLoading, error, networkStatus }) => {
                    if (jiraIssueLoading || networkStatus === 4) return <Loading />;
                    if (error) return `Error! ${error.message}`;
                    return (
                        <InlineEdit
                            className="link-components-inline-edit"
                            readViewFitContainerWidth
                            editView={(fieldProps) => (
                                <VirtualComponentSelect
                                    {...fieldProps}
                                    className="link-components-select"
                                    classNamePrefix="link-components-select"
                                    isMulti
                                    autoFocus
                                    openMenuOnFocus
                                    closeMenuOnSelect={false}
                                    value={linkedComponentsOnEdit}
                                    onChange={setLinkedComponentsOnEdit}
                                    defaultOptions={
                                        componentParentsDefaultOptions?.length
                                            ? componentParentsDefaultOptions.map(parseVirtualComponentToOption)
                                            : true
                                    }
                                    projectKey={projectKey}
                                    menuInDocumentFlow
                                />
                            )}
                            readView={() =>
                                linkedComponentsOnEdit?.length ? (
                                    <DefaultView className="link-components-view">
                                        <TagGroup>
                                            {linkedComponentsOnEdit.map((component) => (
                                                <SimpleTag key={component.value} text={component.label} />
                                            ))}
                                        </TagGroup>{' '}
                                    </DefaultView>
                                ) : (
                                    <DefaultNoneView>
                                        <NoneView>Click to select components</NoneView>
                                    </DefaultNoneView>
                                )
                            }
                            onConfirm={handleConfirm}
                            onCancel={handleCancel}
                        />
                    );
                }}
            </Query>
        </Wrapper>
    );
};

export default compose(graphql(UPDATE_JIRA_ISSUE, { name: 'updateJiraIssue' }))(LinkComponents);
